/**
 * The weekly chest celebration (specs/home-page AC-23): shown once when the chest was granted and
 * not yet acknowledged; dismissing acknowledges it server-side so it never replays on any device.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { postChestSeen } from '../helpers/homeApi';
import { homeQueryKeys } from '../constants/homeQueryKeys';
import type { WeeklyQuestsView } from '../types/home.types';

export interface UseChestCelebrationProps {
  quests: WeeklyQuestsView | null;
  userId: string | null;
}

export interface UseChestCelebrationReturn {
  isOpen: boolean;
  rewardCoins: number;
  dismiss: () => void;
}

export const useChestCelebration = ({ quests, userId }: UseChestCelebrationProps): UseChestCelebrationReturn => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  // Closes the modal immediately; the server ack + refetch follow so a slow network never re-opens it.
  const [dismissedWeek, setDismissedWeek] = useState<string | null>(null);

  const weekKey = quests?.weekKey ?? null;
  const chest = quests?.chest ?? null;
  const isOpen = Boolean(chest?.grantedAt && !chest.seenAt && weekKey && dismissedWeek !== weekKey);

  const dismiss = useCallback(() => {
    if (!weekKey) return;
    setDismissedWeek(weekKey);
    void postChestSeen(apiClient)
      .catch(() => null)
      .finally(() => {
        void queryClient.invalidateQueries({ queryKey: homeQueryKeys.quests(userId) });
      });
  }, [apiClient, queryClient, userId, weekKey]);

  return { isOpen, rewardCoins: chest?.rewardCoins ?? 0, dismiss };
};
